"use client";

import {
    FC,
    RefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import classNames from "classnames";
import { INotificationResponse } from "@finbackoffice/clientbff-client";
import Link from "next/link";
import { DAY_IN_MS, HOUR_IN_MS } from "@finbackoffice/fe-core";
import {
    BrowserStorageContext,
    BrowserStorageIdsEnum,
    useRuntimeConfig,
} from "@finbackoffice/site-core";

type IStorageNotifications = {
    [key: string]: { id: number; last_seen_timestamp: number };
};
import { IModalForwardRefPropsV2 } from "contexts";
import Modal from "components/base/modal/Modal";
import { ModalCloseButtonType } from "components/base/modal/components/ModalCloseButton";
import Img from "components/base/img/Img";
import { AssetsDirectory } from "@finbackoffice/enums";
import { ModalStyleTypeEnum } from "components/base/modal/constants/modal-config";
import { ModalTypes } from "utils/constants";
import { Carousel, CarouselSlide } from "components/base/carousel/Carousel";
import styles from "./site-notifications-modal.module.sass";

type IProps = {
    notifications: INotificationResponse[];
};

const SiteNotificationsModal: FC<IProps> = ({ notifications }) => {
    const { get, set } = useContext(BrowserStorageContext);
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");

    const notificationsMemo = useMemo(() => {
        const storageNotifications = get(BrowserStorageIdsEnum.SITE_NOTIFICATIONS) ?? {};

        return notifications.filter(
            (item) =>
                !storageNotifications[item.id] ||
                Date.now() - storageNotifications[item.id]?.last_seen_timestamp >
                    COMMON_SITE_CONFIGS.siteNotificationShowInterval * HOUR_IN_MS,
        );
    }, [get, notifications, COMMON_SITE_CONFIGS.siteNotificationShowInterval]);

    const ref: RefObject<IModalForwardRefPropsV2 | null> = useRef(null);

    const [viewed, setViewed] = useState<number[]>([]);
    const [title, setTitle] = useState<string>(notificationsMemo[0]?.subject);

    useEffect(() => {
        if (notificationsMemo.length > 0) {
            ref.current?.open();
        }
    }, [notificationsMemo]);

    const updateViewedNotifications = useCallback(
        (currentSlideIndex: number) => {
            setViewed((state) => {
                const id = notificationsMemo[currentSlideIndex].id;
                if (state.includes(id)) return state;
                return [...state, id];
            });
        },
        [notificationsMemo],
    );

    const handleOnSelectCallback = useCallback(
        (nextSlide: number) => {
            setTitle(notificationsMemo[nextSlide].subject);
        },
        [notificationsMemo],
    );

    const handleOnClose = useCallback(() => {
        const storageNotifications: IStorageNotifications =
            get(BrowserStorageIdsEnum.SITE_NOTIFICATIONS) ?? {};

        Object.values(storageNotifications).forEach((item) => {
            if (Date.now() - item.last_seen_timestamp > 30 * DAY_IN_MS) {
                delete storageNotifications[item.id];
            }
        });

        viewed.forEach((id) => {
            storageNotifications[id] = {
                id,
                last_seen_timestamp: Date.now(),
            };
        });

        set(BrowserStorageIdsEnum.SITE_NOTIFICATIONS, JSON.stringify(storageNotifications));
    }, [get, set, viewed]);

    const carouselSettings = useMemo(() => ({ loop: true }), []);

    return (
        <Modal
            ref={ref}
            styleClass={styles.siteNotificationsModal}
            type={ModalTypes.SITE_NOTIFICATIONS}
            closeButtonVariant={ModalCloseButtonType.corner}
            styleConfigType={ModalStyleTypeEnum.Notification}
            animateVariant="opacity"
            closeButton
            priority
            mask={false}
            onClose={handleOnClose}>
            <section className={styles.siteNotifications} data-testid="site-notifications-modal">
                <div className={styles.header}>
                    <Link href="/">
                        <Img
                            source={`/${AssetsDirectory.Mobile}/logo-notification.svg`}
                            alt={`${COMMON_SITE_CONFIGS.appName} logo`}
                            width={0}
                            height={0}
                            style={{ width: "100%", height: "auto" }}
                            prefixAppName
                        />
                    </Link>
                </div>
                <div className={styles.content}>
                    <div className={styles.title}>
                        <span>{title}</span>
                    </div>
                    <Carousel
                        wrapperClassName={styles.notificationsCarousel}
                        showDots={notificationsMemo.length > 1}
                        options={carouselSettings}
                        afterScrollCallback={updateViewedNotifications}
                        onInitCallback={updateViewedNotifications}
                        onSelectCallback={handleOnSelectCallback}>
                        {notificationsMemo.map((item) => (
                            <CarouselSlide key={item.id}>
                                <div
                                    key={item.id}
                                    className={classNames("slider-animate-zoom", styles.item)}>
                                    <p
                                        className="icon-notification"
                                        dangerouslySetInnerHTML={{ __html: item.body }}
                                    />
                                </div>
                            </CarouselSlide>
                        ))}
                    </Carousel>
                </div>
            </section>
        </Modal>
    );
};

export default SiteNotificationsModal;
